import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Music2, ChevronDown } from "lucide-react";

const TRACKS = [
  { id: "chalisa", title: "Hanuman Chalisa", sub: "Kainchi Dham singers — Baba Neem Karoli", videoId: "u1EOmLuWbrY" },
  { id: "aarti", title: "Neem Karoli Baba Aarti", sub: "Jai Baba Neem Karoli · 3-min aarti", videoId: "P5Uen8yIfQc" },
  { id: "sitaram", title: "Sita-Ram · Krishna Das", sub: "33-min music meditation", videoId: "rScK5tlnfEY" },
];

let ytApiPromise;
function loadYouTubeAPI() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve(window.YT);
    };
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.onerror = () => reject(new Error("YouTube player failed to load"));
      document.head.appendChild(script);
    }
  });
  return ytApiPromise;
}

export default function ChantingPlayer() {
  const hostRef = useRef(null);
  const ytRef = useRef(null);
  const pendingVideoRef = useRef(null);
  const mountedRef = useRef(true);
  const [trackId, setTrackId] = useState("chalisa");
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      ytRef.current?.destroy();
      ytRef.current = null;
    };
  }, []);

  // Create the visible YouTube player the first time the panel opens
  useEffect(() => {
    if (!expanded || ytRef.current) return;
    let cancelled = false;
    loadYouTubeAPI()
      .then((YT) => {
        if (cancelled || !mountedRef.current || !hostRef.current) return;
        ytRef.current = new YT.Player(hostRef.current, {
          width: "200",
          height: "200",
          videoId: TRACKS.find((t) => t.id === trackId).videoId,
          playerVars: { enablejsapi: 1, origin: window.location.origin, playsinline: 1, autoplay: 0, controls: 1, rel: 0 },
          events: {
            onReady: (e) => {
              e.target.setVolume(70);
              if (!mountedRef.current) return;
              setReady(true);
              if (pendingVideoRef.current) {
                e.target.loadVideoById(pendingVideoRef.current);
                pendingVideoRef.current = null;
              }
            },
            onStateChange: (e) => {
              if (mountedRef.current) setPlaying(e.data === YT.PlayerState.PLAYING);
            },
          },
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const selectTrack = (id) => {
    setTrackId(id);
    setExpanded(true);
    const videoId = TRACKS.find((t) => t.id === id).videoId;
    if (ready && ytRef.current?.loadVideoById) {
      ytRef.current.loadVideoById(videoId); // tap gesture → allowed to play
    } else {
      pendingVideoRef.current = videoId;
    }
  };

  const toggle = () => {
    if (!expanded) {
      setExpanded(true);
      return;
    }
    if (!ready || !ytRef.current) return;
    if (playing) ytRef.current.pauseVideo();
    else ytRef.current.playVideo();
  };

  const collapse = () => {
    ytRef.current?.pauseVideo(); // never leave a hidden player playing
    setExpanded(false);
  };

  const current = TRACKS.find((t) => t.id === trackId);

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
      <AnimatePresence>
        {expanded && (
          <motion.div
            key="kirtan-panel"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="w-64 rounded-2xl border border-amber-900/25 bg-[#1C1917]/95 backdrop-blur-md p-4 shadow-2xl shadow-stone-950/40"
            data-testid="kirtan-panel"
          >
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/90 font-semibold mb-3">Gentle Kirtan</p>
            <div className="space-y-2">
              {TRACKS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`chanting-track-${t.id}`}
                  onClick={() => selectTrack(t.id)}
                  className={`w-full text-left rounded-xl border px-3.5 py-2.5 transition-all duration-300 ${
                    trackId === t.id ? "border-amber-500/60 bg-amber-500/10" : "border-stone-700 hover:border-amber-700/50"
                  }`}
                >
                  <span className="block text-sm font-display text-amber-50">{t.title}</span>
                  <span className="block text-[10px] text-stone-400 mt-0.5">{t.sub}</span>
                </button>
              ))}
            </div>

            {/* Visible YouTube player — required by YouTube embed policy; plays the selected track */}
            <div className="mt-3 flex flex-col items-center" data-testid="youtube-kirtan-frame">
              <div ref={hostRef} className="rounded-xl overflow-hidden" />
              {!ready && <p className="text-[11px] text-stone-500 mt-2">Loading YouTube player…</p>}
              <p className="text-[10px] text-stone-500 mt-2 text-center">Streams from YouTube with love — full credit to the artists</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center gap-2">
        <motion.button
          type="button"
          data-testid="kirtan-expand-toggle"
          onClick={() => (expanded ? collapse() : setExpanded(true))}
          aria-label={expanded ? "Close kirtan player" : "Open kirtan player"}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-center w-10 h-10 rounded-full border border-amber-900/25 bg-[#1C1917]/95 backdrop-blur-md text-amber-400 shadow-xl shadow-stone-950/40 hover:scale-105 active:scale-95 transition-transform duration-300"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <Music2 className="h-4 w-4" />}
        </motion.button>

        <motion.button
          type="button"
          data-testid="chanting-player-toggle"
          onClick={toggle}
          aria-label={playing ? `Pause ${current.title}` : `Play ${current.title}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3 rounded-full border border-amber-900/25 bg-[#1C1917]/95 backdrop-blur-md pl-3 pr-5 py-2.5 shadow-2xl shadow-stone-950/40 hover:scale-[1.04] active:scale-95 transition-transform duration-300"
        >
          <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-b from-amber-500 to-orange-700 text-white shrink-0">
            {playing && <span className="absolute inset-0 rounded-full bg-amber-500/50 animate-ping" />}
            {playing ? <Pause className="h-4 w-4 relative" /> : <Play className="h-4 w-4 relative ml-0.5" />}
          </span>
          <span className="text-left">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-amber-400/90 font-semibold">
              {playing ? "Now Chanting" : "Gentle Kirtan"}
            </span>
            <span className="block text-sm font-display text-amber-50 leading-tight">{current.title}</span>
          </span>
          {playing && (
            <span className="flex items-end gap-[3px] h-4 ml-1" data-testid="chanting-eq" aria-hidden="true">
              <span className="eq-bar" style={{ animationDelay: "0ms" }} />
              <span className="eq-bar" style={{ animationDelay: "180ms" }} />
              <span className="eq-bar" style={{ animationDelay: "360ms" }} />
              <span className="eq-bar" style={{ animationDelay: "120ms" }} />
            </span>
          )}
        </motion.button>
      </div>
    </div>
  );
}
